import { EXCHANGE_DECIMALS } from 'config/constants';
import { BigNumberClass } from 'lib/BigNumber';
import { trimTo8Decimals } from 'lib/numbers';

/**
 * WARNING: this might round number up. formatBignumberJs might be
 * better option for rounding down
 *
 * @deprecated Note that maximumSignificantDigits would format 123456789123 to 123456780000
 *  - so ending digits are set to 0, similar with visibleDecimals
 */
export function formatNumberToSignificantDigits(
  value?: number | string | bigint,
  digits = 8,
  opts: Intl.NumberFormatOptions = {}
): string {
  if (value === null || value === undefined) {
    return '';
  }
  const result = typeof value === 'string' ? value.replace(/,/g, '') : value;
  // console.log(
  //   'Format Number: ',
  //   result,
  //   new Intl.NumberFormat('en-US', {
  //     maximumSignificantDigits: digits,
  //   }).format(typeof result === 'string' ? Number(result) : result)
  // );
  return new Intl.NumberFormat('en-US', {
    maximumSignificantDigits: digits,
    ...opts,
  }).format(typeof result === 'string' ? Number(result) : result);
}

/**
 * Use this placeholder for all numbers received preformatted as string.
 * We do not apply formatting to make sure web client is fast as possilbe.
 *
 * Why to use this?
 * In case we would like to apply different formatting globally,
 * we can easily find all usages.
 *
 */
// export function formatApiNumber(value?: string): string {
//   if (!value) {
//     return '';
//   }
//   let result = value;
//   // Then we need also add separators for thousands
//   // Length of number is 8 decimals + 1 dot + 4 char int number
//   if (result.length >= 13) {
//     result = Number(result).toLocaleString('en', {
//       minimumFractionDigits: 8,
//       maximumFractionDigits: 8,
//     });
//   }

//   return result;
// }

// TODO: consider formatNumber refactor using https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
export function formatNumber(
  value?: string | number | bigint | BigNumberClass,
  config: {
    totalDigits?: number;
    fixedDecimals?: number;
    thousandsSeparator?: boolean;
  } = {}
): string {
  if (!value && value !== 0) {
    return '';
  }
  const number =
    typeof value === 'number'
      ? value
      : // Would be good to do more efficient formatting for bignumber using it's utilities toPrecision etc
      value instanceof BigNumberClass
      ? value.toNumber()
      : Number(value);
  if (isNaN(number)) {
    return '';
  }
  const decimals = config.fixedDecimals === 0 ? 0 : config.fixedDecimals || 8;
  const decimalsFinal = config.totalDigits
    ? Math.max(
        Math.min(
          decimals,
          config.totalDigits - String(number).split('.')[0].length
        ),
        0
      )
    : decimals;
  const numberString =
    decimalsFinal === 8
      ? // Make sure we always round down for 8 decimal numbers
        Number(trimTo8Decimals(number)).toFixed(8)
      : // TODO: it's not truncating but rounding here
        number.toFixed(decimalsFinal);

  if (config.thousandsSeparator === false) {
    return numberString;
  }

  // Add thousands separators
  const numberSplit = numberString.split('.');
  return (
    numberSplit[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
    (numberSplit[1] ? '.' + numberSplit[1] : '')
  );
}

/**
 * Updated number format to display 1 or more decimals (trim all extra),
 * and use thousands grouping
 */
export const formatNumber2 = (
  value: number | string,
  maxDecimals: number | undefined = EXCHANGE_DECIMALS,
  minDecimals = 1,
  thousandsSeparator = true
): string => {
  return Intl.NumberFormat('en', {
    useGrouping: thousandsSeparator,
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: minDecimals,
  }).format(typeof value === 'string' ? Number(value) : value);
};

/** Price specific formatting, which makes usage of formatNumber less chatty */
export const formatPrice = (
  value: string | number | BigNumberClass,
  decimals: number | undefined
) => {
  if (decimals === undefined) {
    return '';
  }
  return formatNumber(value, {
    fixedDecimals: decimals,
    thousandsSeparator: true,
  });
};

/** Similar to "formatPrice" but will remove trailing 0s but keep at least 1 decimal */
export const shortPriceFormat = (
  value: number | string,
  maxDecimals: number | undefined,
  minDecimals = 0
) => {
  return Intl.NumberFormat('en', {
    useGrouping: true,
    maximumFractionDigits: maxDecimals,
    // Make sure min decimals is always smaller, or equal than max decimals
    minimumFractionDigits: Math.min(
      maxDecimals !== undefined ? maxDecimals : minDecimals,
      minDecimals
    ),
  }).format(Number(value));
};

/** Format percentage (2 decimal places) */
export const formatPercentage = (
  value?: string | number | bigint | BigNumberClass | null,
  /** Most percentage API values are defined as "1" for "100%" */
  multiplyBy100 = false
) =>
  value === null || value === undefined
    ? ''
    : formatNumber(multiplyBy100 ? Number(value) * 100 : value, {
        fixedDecimals: 2,
        thousandsSeparator: false,
      });

export function formatUsd(
  value?: string | number,
  { decimals } = { decimals: 2 }
): string {
  return formatNumber(value, {
    fixedDecimals: decimals,
    thousandsSeparator: true,
  });
}

/**
 * Format USD with dollar sign
 * @example
 *  formatUsdWithSign(0.5) // outputs $0.50
 *  formatUsdWithSign(-5) // outputs -$5.00
 */
export function formatUsdWithSign(
  value?: string | number,
  { decimals } = { decimals: 2 }
): string {
  const valueNum = Number(value);
  const isNegative = valueNum < 0;
  return `${isNegative ? '-' : ''}$${formatUsd(
    // Remove extra minus sign if needed
    isNegative ? valueNum * -1 : valueNum,
    {
      decimals,
    }
  )}`;
}

/**
 * eg. '123.456000' is split into ['123.456', '000']
 */
export const splitNumberEndingZeros = (
  formattedNumber: string
): [string, string] => {
  if (!formattedNumber || !formattedNumber.includes('.')) {
    return [formattedNumber, ''];
  }
  const matched = formattedNumber.match(/(.*[^0])(0+)$/);
  if (matched) {
    return [matched[1], matched[2]];
  }
  return [formattedNumber, ''];
};

/**
 * Round number to max defined decimals
 * eg. to 2 decimals like 0.005123 to 0.01, 0.001 to 0
 */
export const roundNumberAtDecimals = (number: number, decimals = 8) =>
  number ? Math.round(number * 10 ** decimals) / 10 ** decimals : number;

/**
 * Translate placeholder
 */
export const t = (s1: string, params: Record<string, string | number> = {}) => {
  const s = s1.replace(/.*:::/, '').replace(/.*::/, '');
  return Object.keys(params).length === 0
    ? s
    : Object.keys(params).reduce((acc, paramKey) => {
        const param = params[paramKey];
        return acc.replace(`{{${paramKey}}}`, param.toString());
      }, s);
};

const makeCompactFormatter = (decimals?: 1 | 2, roundingMode?: 'trunc') =>
  window.Intl.NumberFormat('en', {
    notation: 'compact',
    ...(decimals && {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
    ...(roundingMode && { roundingMode }),
  });

const compactFormatters = {
  auto: makeCompactFormatter(),
  1: makeCompactFormatter(1),
  2: makeCompactFormatter(2),
};

// Truncates towards zero so the value is never rounded up (e.g. 1.59M -> 1.5M).
const compactFormattersTruncated = {
  auto: makeCompactFormatter(undefined, 'trunc'),
  1: makeCompactFormatter(1, 'trunc'),
  2: makeCompactFormatter(2, 'trunc'),
};

/**
 * Based on https://stackoverflow.com/questions/9461621/format-a-number-as-2-5k-if-a-thousand-or-more-otherwise-900
 * eg. 1.5k = 1500
 *
 * Pass `decimals` to force an exact number of fraction digits (eg. 10_500_000
 * with decimals 1 -> "10.5M"); omit it to keep Intl's default compact rounding.
 *
 * Pass `rounding: 'truncate'` to truncate towards zero so the value is never
 * rounded up (eg. 1.59M stays "1.5M" instead of becoming "1.6M").
 */
export const abbreviateNumber = (
  number: number,
  decimals?: 1 | 2,
  rounding?: 'truncate'
) =>
  (rounding === 'truncate' ? compactFormattersTruncated : compactFormatters)[
    decimals ?? 'auto'
  ].format(number);

export function buildMarketPair(base: string, quote: string): string;
export function buildMarketPair(
  base?: string,
  quote?: string
): string | undefined;
export function buildMarketPair(base?: string, quote?: string) {
  return base && quote ? `${base}-${quote}` : undefined;
}
